import React, { useState } from 'react';
import { Button, TextField, Grid, FormControlLabel, CircularProgress, Checkbox, Typography, styled, Select, MenuItem, FormHelperText, IconButton } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import Card from '@mui/material/Card';
import SnackbarComponent from '../../../utils/SnackbarComponent';
import UploadIcon from '@mui/icons-material/Upload';
import { Clear } from '@mui/icons-material';
import { createCompany } from '../../../services/apiservice';
import { Helmet } from 'react-helmet-async';

const RedAsterisk = styled('span')({
  color: 'red',
});

const FileInputWrapper = styled('label')({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  width: '100%',
});

const UploadInput = styled('input')({
  display: 'none',
});

const UploadText = styled('span')(({ theme }) => ({
  marginTop: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const ClickableText = styled(Typography)({
  cursor: 'pointer',
  color: 'blue',
  textDecoration: 'underline',
});

function CompanyForm() {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);
  const [imageName, setImageName] = useState(null);

  const validationSchema = Yup.object({
    CompanyName: Yup.string().required('Company Name is required'),
    CompanyType: Yup.string().required('Company Type is required'),
    CompanyIndustry: Yup.string().required('Company Industry is required'),
    CompanyHeadquarters: Yup.string().required('Company Headquarters is required'),
    CompanyWebsiteURL: Yup.string()
      .required('Company Website URL is required')
      .test('url', 'Invalid URL', url => {
        const urlRegex = /^(https?|ftp):\/\/[^\s]+$/;
        return urlRegex.test(url);
      }),
    CompanyFoundedYear: Yup.number().nullable(),
    CompanyRevenue: Yup.number().nullable(),
    CompanyContactInfo: Yup.string().nullable(),
    CompanyDescription: Yup.string().nullable(),
    CompanyIsConsultingCompany: Yup.string().nullable(),
    ConsultingCompanyID: Yup.string().nullable(),
    IssuerID: Yup.string().nullable(),
    CompanyLogoURL: Yup.mixed().nullable()
      .test(
        'fileSize',
        'Image size is too large, max size is 2MB',
        value => !value || (value && value.size <= 2 * 1024 * 1024)
      )
      .test(
        'fileType',
        'Only image formats are allowed (jpg, png, jpeg)',
        value => !value || (value && ['image/jpg', 'image/jpeg', 'image/png'].includes(value.type))
      ),
  });

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleClearImage = (setFieldValue) => {
    setImageName(null);
    setFieldValue('CompanyLogoURL', null);
  };

  return (
    <>
      <Helmet>
        <title>Company Form</title>
      </Helmet>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <Formik
        initialValues={{
          CompanyName: '',
          CompanyType: '',
          CompanyIndustry: '',
          CompanyHeadquarters: '',
          CompanyWebsiteURL: '',
          CompanyFoundedYear: null,
          CompanyRevenue: null,
          CompanyContactInfo: null,
          CompanyDescription: null,
          CompanyIsConsultingCompany: '',
          ConsultingCompanyID: null,
          IssuerID: null,
          CompanyLogoURL: null,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          try {
            const submittedValues = { ...values };
            submittedValues.CompanyIsConsultingCompany = values.CompanyIsConsultingCompany === 'Yes';
            const response = await createCompany(submittedValues);

            if (response.success) {
              setSnackbarMessage('Company Added successfully!');
              setSnackbarSeverity('success');
              resetForm();
            } else {
              setSnackbarMessage('Failed to Add Company.');
              setSnackbarSeverity('error');
            }

            setSnackbarOpen(true);
          } catch (error) {
            console.error('Submission error:', error);
            const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          } finally {
            setSubmitting(false);
          }

        }}
      >
        {({ errors, touched, setFieldValue, setFieldError, isSubmitting, values }) => (
          <Card sx={{ width: '100%', padding: 2 }}>
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    label={
                      <>
                        {'Company Name'} <RedAsterisk>*</RedAsterisk>
                      </>
                    }
                    name="CompanyName"
                    fullWidth
                    error={touched.CompanyName && Boolean(errors.CompanyName)}
                    helperText={touched.CompanyName && errors.CompanyName}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={Select}
                    name="CompanyType"
                    fullWidth
                    error={touched.CompanyType && Boolean(errors.CompanyType)}
                    displayEmpty
                    renderValue={(value) => (value ? value : (
                      <Typography variant="body1" color="text.secondary">
                        Company Type <RedAsterisk>*</RedAsterisk>
                      </Typography>
                    ))}
                  >
                    <MenuItem value="Public">Public</MenuItem>
                    <MenuItem value="Private">Private</MenuItem>
                  </Field>
                  <FormHelperText sx={{ marginLeft: 1.5 }} error={touched.CompanyType && Boolean(errors.CompanyType)}>
                    {touched.CompanyType && errors.CompanyType}
                  </FormHelperText>
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    label={
                      <>
                        {'Company Industry'} <RedAsterisk>*</RedAsterisk>
                      </>
                    }
                    name="CompanyIndustry"
                    fullWidth
                    error={touched.CompanyIndustry && Boolean(errors.CompanyIndustry)}
                    helperText={touched.CompanyIndustry && errors.CompanyIndustry}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    label={
                      <>
                        {'Company Headquarters'} <RedAsterisk>*</RedAsterisk>
                      </>
                    }
                    name="CompanyHeadquarters"
                    fullWidth
                    error={touched.CompanyHeadquarters && Boolean(errors.CompanyHeadquarters)}
                    helperText={touched.CompanyHeadquarters && errors.CompanyHeadquarters}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    label=
                    {
                      <>
                        {'Company Website URL'} <RedAsterisk>*</RedAsterisk>
                      </>
                    }
                    name="CompanyWebsiteURL"
                    fullWidth
                    error={touched.CompanyWebsiteURL && Boolean(errors.CompanyWebsiteURL)}
                    helperText={touched.CompanyWebsiteURL && errors.CompanyWebsiteURL}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    label="Company Description"
                    name="CompanyDescription"
                    fullWidth
                    error={touched.CompanyDescription && Boolean(errors.CompanyDescription)}
                    helperText={touched.CompanyDescription && errors.CompanyDescription}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    label="Company Contact Info"
                    name="CompanyContactInfo"
                    fullWidth
                    error={touched.CompanyContactInfo && Boolean(errors.CompanyContactInfo)}
                    helperText={touched.CompanyContactInfo && errors.CompanyContactInfo}
                  />
                </Grid>
                <Grid item xs={3}>
                  <FileInputWrapper>
                    <UploadInput
                      accept="image/*"
                      id="CompanyLogoURL"
                      name="CompanyLogoURL"
                      type="file"
                      onChange={(event) => {
                        const file = event.currentTarget.files[0];
                        setFieldValue("CompanyLogoURL", file);
                        setImageName(file ? file.name : null);
                      }}
                    />
                    <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                      Upload Company Logo
                    </Button>
                  </FileInputWrapper>
                  <UploadText style={{ marginLeft: '8px' }}>
                    {imageName ? (
                      <>
                        {imageName}
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            handleClearImage(setFieldValue);
                          }}
                          size="small"
                          color="error"
                          aria-label="clear"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      'No file chosen'
                    )}
                  </UploadText>
                  {errors.CompanyLogoURL && (
                    <Typography color="error">{errors.CompanyLogoURL}</Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <ClickableText
                    onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
                  >
                    {showAdditionalDetails ? 'Hide More Details' : 'Add More Details'}
                  </ClickableText>
                </Grid>


                {showAdditionalDetails && (
                  <>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        label='Company Founded Year (Optional)'
                        name="CompanyFoundedYear"
                        type="number"
                        fullWidth
                        error={touched.CompanyFoundedYear && Boolean(errors.CompanyFoundedYear)}
                        helperText={touched.CompanyFoundedYear && errors.CompanyFoundedYear}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <div>
                        <label>Is Consulting Company</label>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.CompanyIsConsultingCompany === 'Yes'}
                              onChange={() => setFieldValue('CompanyIsConsultingCompany', values.CompanyIsConsultingCompany === 'Yes' ? 'No' : 'Yes')}
                              color="primary"
                            />
                          }
                          label="Yes"
                        />
                        {touched.CompanyIsConsultingCompany && errors.CompanyIsConsultingCompany && (
                          <div style={{ color: 'red' }}>{errors.CompanyIsConsultingCompany}</div>
                        )}
                      </div>
                    </Grid>
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        label='Company Revenue (Optional) '
                        name="CompanyRevenue"
                        type="number"
                        fullWidth
                        error={touched.CompanyRevenue && Boolean(errors.CompanyRevenue)}
                        helperText={touched.CompanyRevenue && errors.CompanyRevenue}
                      />
                    </Grid>
                    {values.CompanyIsConsultingCompany === 'Yes' && (
                      <Grid item xs={6}>
                        <Field
                          as={TextField}
                          label="Consulting Company ID (Optional)"
                          name="ConsultingCompanyID"
                          fullWidth
                          error={touched.ConsultingCompanyID && Boolean(errors.ConsultingCompanyID)}
                          helperText={touched.ConsultingCompanyID && errors.ConsultingCompanyID}
                        />
                      </Grid>

                    )}
                  </>
                )}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={isSubmitting}
                    startIcon={isSubmitting && <CircularProgress size={24} color="inherit" />}
                  >
                    {isSubmitting ? 'Creating Company...' : 'Create Company'}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          </Card>
        )}
      </Formik>
    </>
  );
}

export default CompanyForm;
